import React, { useCallback, useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button, Card, Input, Spinner, SectionHeader } from '../components/common';
import { ApiKey, ApiKeyCreated } from '../types';
import { apiKeysApi } from '../api/apiKeys';

function formatDate(value: string | null): string {
  if (!value) return 'never';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'never';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function SettingsPage() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    try {
      const data = await apiKeysApi.listKeys();
      setKeys(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load API keys';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!label.trim() || creating) return;

    setCreating(true);
    setError(null);
    setCopied(false);
    try {
      const created = await apiKeysApi.createKey(label.trim());
      setCreatedKey(created);
      setLabel('');
      await loadKeys();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create API key';
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
    } catch {
      setError('Could not copy to clipboard — select the key and copy it manually.');
    }
  };

  const handleRevoke = async (keyId: string) => {
    setRevokingId(keyId);
    setError(null);
    try {
      await apiKeysApi.revokeKey(keyId);
      if (createdKey?.id === keyId) {
        setCreatedKey(null);
      }
      await loadKeys();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revoke API key';
      setError(message);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in">
        <SectionHeader
          eyebrow="settings"
          title="API keys"
          description="Personal API keys let integrations — like the Noctua Obsidian plugin — send text to your podcast feed. Treat them like passwords."
          className="mb-12"
        />

        {error && (
          <div
            role="alert"
            className="mb-8 p-6 bg-destructive/10 border border-destructive/20 rounded-sm animate-fade-in"
          >
            <p className="font-body text-destructive">{error}</p>
          </div>
        )}

        {/* Create key */}
        <Card className="mb-8">
          <h3 className="font-mono text-xs tracking-wide uppercase text-muted-foreground mb-4">
            Create a key
          </h3>
          <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-4 sm:items-end">
            <div className="flex-1">
              <Input
                label="Label"
                placeholder="e.g. Obsidian plugin"
                value={label}
                maxLength={100}
                onChange={(event) => setLabel(event.target.value)}
              />
            </div>
            <Button type="submit" disabled={!label.trim()} loading={creating}>
              create key
            </Button>
          </form>
        </Card>

        {/* One-time display of a freshly created key */}
        {createdKey && (
          <Card className="mb-8 border-primary/40 bg-primary/5 animate-fade-in">
            <h3 className="font-mono text-xs tracking-wide uppercase text-muted-foreground mb-3">
              Your new key — copy it now
            </h3>
            <p className="font-body text-sm text-muted-foreground mb-4">
              This is the only time the full key is shown. Store it somewhere safe.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <code
                data-testid="created-key"
                className="flex-1 font-mono text-sm bg-card border border-border rounded-sm px-4 py-3 break-all"
              >
                {createdKey.key}
              </code>
              <Button variant="secondary" onClick={handleCopy}>
                {copied ? 'copied' : 'copy'}
              </Button>
            </div>
          </Card>
        )}

        {/* Key list */}
        <SectionHeader
          eyebrow="active keys"
          title="Your keys"
          level="h2"
          size="md"
          className="mb-6"
        />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : keys.length === 0 ? (
          <p className="font-body text-muted-foreground py-4">
            No API keys yet. Create one above to connect an integration.
          </p>
        ) : (
          <div className="space-y-4">
            {keys.map((key) => (
              <Card key={key.id}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-medium text-foreground">{key.label}</p>
                    <p className="font-mono text-xs text-muted-foreground mt-1">
                      {key.keyPrefix}… · created {formatDate(key.createdAt)} · last used{' '}
                      {formatDate(key.lastUsedAt)}
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={revokingId === key.id}
                    onClick={() => handleRevoke(key.id)}
                  >
                    revoke
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
